import { StatusCodes } from 'http-status-codes';
import { AppErrorCode } from './error-codes';

export type AppErrorOptions = { name: string; message: string; code: string; HTTPStatus?: number; isTrusted?: boolean; cause?: Error };

/**
 * The base class for all app errors.
 *
 * **IMPORTANT** If you write your own error handler, you should **not** render the error
 * without using a templating engine that will properly escape it for other HTML contexts first.
 *
 * Note that the error message of the {@link AppError.cause | underlying error} is **not** escaped
 * in any way, so do **not** render it without escaping it first!
 *
 * @class AppError
 * @extends {Error}
 * @description Represents a custom error class for application-specific errors.
 */
export class AppError extends Error {
	// The error class name.
	public readonly name: string;
	/**
	 * NOTE:
	 * We intentionally use `digest` because it survives Next.js production error sanitization.
	 * This relies on internal behavior.
	 */
	public readonly digest: string;
	// A descriptive message about the error.
	public readonly message: string;
	/**
	 * A machine-readable error code that remains stable within a major version of the SDK. You
	 * should rely on this error code to handle errors. In contrast, the error message is not part of
	 * the API and can change anytime. Do **not** parse or otherwise rely on the error message to
	 * handle errors.
	 */
	public readonly code: string;
	// The HTTP status code associated with the error (default: 500).
	public readonly HTTPStatus?: number;
	// Indicates whether the error originates from trusted code (default: true).
	public readonly isTrusted?: boolean;
	/**
	 * The underlying cause of the error, if any, this is usually the error object.
	 *
	 * You should **not** render this error without using a templating engine that will properly escape it for other
	 * HTML contexts first.
	 */
	public readonly cause?: Error;

	constructor(options: AppErrorOptions) {
		super();
		this.name = options.name;
		this.digest = options.name;
		this.message = options.message;
		this.code = options.code || AppErrorCode.APP_UNKNOWN_ERROR;
		this.HTTPStatus = options.HTTPStatus || StatusCodes.INTERNAL_SERVER_ERROR;
		this.isTrusted = options.isTrusted;
		// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
		this.cause = options.cause;
	}
}
